import React, { useEffect, useState } from "react";
import { agentInstanceService } from "../../../services/agentService";
import { toast } from "react-toastify";

const DB_TYPES = ["mssql", "postgres", "mysql"];

const DatabaseTab: React.FC<{ agent: any }> = ({ agent }) => {
    const [form, setForm] = useState<any>({
        dbType: "postgres",
        dbVersion: "",
        host: "",
        port: "",
        database: "",
        username: "",
        password: "",
        ssl: false,
        metadata: {},
    });

    const [loading, setLoading] = useState(false);
    const [existingConfig, setExistingConfig] = useState<any>(null);
    const [metaEntries, setMetaEntries] = useState<{ key: string; value: string }[]>([]);

    useEffect(() => {
        if (agent?.id) loadConfig();
    }, [agent]);

    useEffect(() => {
        if (form.metadata) {
            const entries = Object.entries(form.metadata).map(([key, value]) => ({
                key,
                value: String(value),
            }));
            setMetaEntries(entries.length ? entries : [{ key: "", value: "" }]);
        }
    }, [form.metadata]);

    const loadConfig = async () => {
        try {
            const res: any = await agentInstanceService.getDbConfig(agent.id);
            if (res) {
                setExistingConfig(res);
                setForm({
                    dbType: res.dbType,
                    dbVersion: res.dbVersion || "",
                    host: res.host,
                    port: res.port,
                    database: res.database,
                    username: res.username,
                    password: res.password,
                    ssl: res.ssl,
                    metadata: res.metadata || {}
                });
            }
        } catch (err) {
            // silent (no config yet)
        }
    };

    const handleChange = (key: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [key]: value }));
    };

    const handleMetaChange = (index: number, field: "key" | "value", value: string) => {
        const updated = [...metaEntries];
        updated[index][field] = value;
        setMetaEntries(updated);
    };

    const addMetaField = () => {
        setMetaEntries([...metaEntries, { key: "", value: "" }]);
    };

    const removeMetaField = (index: number) => {
        const updated = metaEntries.filter((_, i) => i !== index);
        setMetaEntries(updated);
    };

    const handleSubmit = async () => {
        if (!agent?.id) return;

        setLoading(true);
        try {
            const metadataObject = metaEntries.reduce((acc: any, curr) => {
                if (!curr.key.trim()) return acc;
                if (curr.key) acc[curr.key] = curr.value;
                return acc;
            }, {});

            const payload = {
                ...form,
                metadata: metadataObject
            };

            if (existingConfig) {
                await agentInstanceService.updateDbConfig(agent.id, payload);
                toast.success("DB Config updated");
            } else {
                await agentInstanceService.createDbConfig({
                    ...payload,
                    agentInstanceId: agent.id,
                });
                toast.success("DB Config created");
            }

            loadConfig();
        } catch (err: any) {
            toast.error(err?.message || "Failed to save DB config");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <h3 className="text-2xl font-bold">Database Configuration</h3>

            {/* DB Type */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-semibold mb-2">DB Type</label>
                    <select
                        value={form.dbType}
                        onChange={(e) => handleChange("dbType", e.target.value)}
                        className="w-full border rounded-lg px-4 py-2"
                    >
                        {DB_TYPES.map((type) => (
                            <option key={type}>{type}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-semibold mb-2">DB Version</label>
                    <input
                        placeholder="e.g. 14.5 / 2019 / 8.0"
                        value={form.dbVersion}
                        onChange={(e) => handleChange("dbVersion", e.target.value)}
                        className="w-full border rounded-lg px-4 py-2"
                    />
                </div>
            </div>

            {/* Host + Port */}
            <div className="grid grid-cols-2 gap-4">
                <input
                    placeholder="Host"
                    value={form.host}
                    onChange={(e) => handleChange("host", e.target.value)}
                    className="border rounded-lg px-4 py-2"
                />
                <input
                    placeholder="Port"
                    value={form.port}
                    onChange={(e) => handleChange("port", Number(e.target.value))}
                    className="border rounded-lg px-4 py-2"
                />
            </div>

            {/* DB Name */}
            <input
                placeholder="Database"
                value={form.database}
                onChange={(e) => handleChange("database", e.target.value)}
                className="w-full border rounded-lg px-4 py-2"
            />

            {/* Username + Password */}
            <div className="grid grid-cols-2 gap-4">
                <input
                    placeholder="Username"
                    value={form.username}
                    onChange={(e) => handleChange("username", e.target.value)}
                    className="border rounded-lg px-4 py-2"
                />
                <input
                    type="password"
                    placeholder="Password"
                    value={form.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    className="border rounded-lg px-4 py-2"
                />
            </div>

            {/* SSL */}
            {/* <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={form.ssl}
                    onChange={(e) => handleChange("ssl", e.target.checked)}
                />
                <span>Enable SSL</span>
            </div> */}

            <div>
                <label className="block text-sm font-semibold mb-2">
                    Additional Connection Params
                </label>

                <div className="space-y-3">
                    {metaEntries.map((entry, index) => (
                        <div key={index} className="grid grid-cols-5 gap-2">
                            <input
                                placeholder="Key (e.g. schema, encrypt)"
                                value={entry.key}
                                onChange={(e) =>
                                    handleMetaChange(index, "key", e.target.value)
                                }
                                className="col-span-2 border rounded-lg px-3 py-2"
                            />

                            <input
                                placeholder="Value"
                                value={entry.value}
                                onChange={(e) =>
                                    handleMetaChange(index, "value", e.target.value)
                                }
                                className="col-span-2 border rounded-lg px-3 py-2"
                            />

                            <button
                                onClick={() => removeMetaField(index)}
                                className="text-red-500"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>

                <button
                    onClick={addMetaField}
                    className="mt-3 text-blue-600"
                >
                    + Add Param
                </button>
            </div>

            {/* Submit */}
            <button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg"
            >
                {loading ? "Saving..." : existingConfig ? "Update" : "Save"}
            </button>
        </div>
    );
};

export default DatabaseTab;
